import { useCallback, useEffect, useMemo, useState } from 'react';
import { PilotCatalogEntry, PilotOnboardingService } from '@services/PilotOnboardingService';
import { Description } from '@shared/utils/DescriptionsHelper';

export type PilotStatus = 'active' | 'lowUsage' | 'noUsage';
export type PilotStatusFilter = 'all' | 'withActivity' | 'noUsage';

export interface PilotCatalogTableRow {
    rowKey: string;
    organizationID: string;
    id: string;
    isIDHighlighted: boolean;
    name: string;
    legalName: string;
    customer: string;
    countryCode: string;
    timezone: string;
    usersLimit: number;
    startDate: string;
    endDate: string;
    creationTimestamp: number;
    creationDate: string;
    creationDateTime: string;
    lastTimeUpdated: string;
    lastTimeUpdatedTimestamp: number | null;
    lastTimeUpdatedDateTime: string;
    totalUsers: string;
    usersLastWeek: string;
    usersLastWeekCount: number;
    usersPreviousWeek: string;
    ticketsLast2Weeks: string;
    ticketsLastWeek: string;
    ticketsPreviousWeek: string;
    status: PilotStatus;
    statusLabel: string;
    pilotSource: string;
}

interface UsePilotOnboardingTableViewModelProps {
    pilotOnboardingService: PilotOnboardingService;
}

export interface ClosePilotResult {
    kind: 'success' | 'error';
    message: string;
}

export interface ConvertPilotResult {
    kind: 'success' | 'error';
    message: string;
}

// A pilot is "active" from this many distinct weekly users; below it usage counts as low.
const ACTIVE_WEEKLY_USERS_THRESHOLD = 3;

export const getPilotStatus = (usersLastWeek: number): PilotStatus => {
    if (usersLastWeek <= 0) {
        return 'noUsage';
    }

    if (usersLastWeek < ACTIVE_WEEKLY_USERS_THRESHOLD) {
        return 'lowUsage';
    }

    return 'active';
};

export const matchesPilotStatusFilter = (status: PilotStatus, filter: PilotStatusFilter): boolean => {
    switch (filter) {
        case 'withActivity':
            return status !== 'noUsage';
        case 'noUsage':
            return status === 'noUsage';
        case 'all':
        default:
            return true;
    }
};

const getPilotStatusLabel = (status: PilotStatus): string => {
    switch (status) {
        case 'active':
            return 'Activo'.localized();
        case 'lowUsage':
            return 'Uso bajo'.localized();
        case 'noUsage':
        default:
            return 'Sin uso'.localized();
    }
};

const getSafeString = (value: unknown, fallback = '') => {
    if (typeof value !== 'string') {
        return fallback;
    }

    const normalized = value.trim();
    return normalized || fallback;
};

// Returns the timezone only when Intl accepts it — the display fallback "N/D" (or any
// invalid value) must never reach toLocaleString, which throws a RangeError on it.
export const getSafeTimezone = (value: unknown): string | undefined => {
    if (typeof value !== 'string' || !value.trim()) {
        return undefined;
    }

    try {
        new Intl.DateTimeFormat('es-ES', { timeZone: value });
        return value;
    } catch {
        return undefined;
    }
};

const formatTimestamp = (value: number | null | undefined, timezone?: string) => {
    if (typeof value !== 'number' || value <= 0) {
        return 'N/D'.localized();
    }

    return Description.Timestamp.timeAgoWithShortDate(value, timezone ?? undefined);
};

const formatTimestampDateTime = (value: number | null | undefined, timezone?: string) => {
    if (typeof value !== 'number' || value <= 0) {
        return 'N/D'.localized();
    }

    return Description.Timestamp.timeAgoWithShortDate(value, timezone ?? undefined);
};

const STALE_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const shouldHighlightPilotID = (pilot: PilotCatalogEntry) => {
    const creationTimestamp = typeof pilot.creationTimestamp === 'number' ? pilot.creationTimestamp : 0;

    if (creationTimestamp <= 0) {
        return false;
    }

    const lastActivityTimestamp = typeof pilot.analytics?.lastTicketTimestamp === 'number'
        ? pilot.analytics.lastTicketTimestamp
        : null;
    const referenceTimestamp = lastActivityTimestamp ?? creationTimestamp;

    return Date.now() - referenceTimestamp > STALE_ACTIVITY_WINDOW_MS;
};

export const comparePilotsByDefaultOrder = (a: PilotCatalogTableRow, b: PilotCatalogTableRow) => {
    return a.creationTimestamp - b.creationTimestamp || a.name.localeCompare(b.name);
};

const normalizePilotRow = (pilot: PilotCatalogEntry, index: number): PilotCatalogTableRow => {
    const usersLastWeekCount = typeof pilot.analytics?.usersLastWeek === 'number'
        ? pilot.analytics.usersLastWeek
        : 0;
    const status = getPilotStatus(usersLastWeekCount);
    const safeTimezone = getSafeTimezone(pilot.timezone);

    return {
        rowKey: getSafeString(pilot.organizationId, `pilot-row-${index}`),
        organizationID: getSafeString(pilot.organizationId),
        id: getSafeString(pilot.organizationId, 'N/D'.localized()),
        isIDHighlighted: shouldHighlightPilotID(pilot),
        name: getSafeString(pilot.name, 'N/D'.localized()),
        legalName: getSafeString(pilot.legalName),
        customer: getSafeString(pilot.customer),
        countryCode: getSafeString(pilot.countryCode, 'N/D'.localized()),
        timezone: getSafeString(pilot.timezone, 'N/D'.localized()),
        usersLimit: typeof pilot.usersLimit === 'number' ? pilot.usersLimit : 0,
        startDate: getSafeString(pilot.startDate),
        endDate: getSafeString(pilot.endDate),
        creationTimestamp: typeof pilot.creationTimestamp === 'number' ? pilot.creationTimestamp : 0,
        creationDate: formatTimestamp(pilot.creationTimestamp, safeTimezone),
        creationDateTime: formatTimestampDateTime(pilot.creationTimestamp, safeTimezone),
        lastTimeUpdated: formatTimestamp(pilot.analytics?.lastTicketTimestamp, safeTimezone),
        lastTimeUpdatedTimestamp: typeof pilot.analytics?.lastTicketTimestamp === 'number'
            ? pilot.analytics.lastTicketTimestamp
            : null,
        lastTimeUpdatedDateTime: formatTimestampDateTime(pilot.analytics?.lastTicketTimestamp, safeTimezone),
            totalUsers: `${pilot.analytics?.usersLast2Weeks ?? 0}`,
            usersLastWeek: `${usersLastWeekCount}`,
            usersLastWeekCount,
            usersPreviousWeek: `${pilot.analytics?.usersPreviousWeek ?? 0}`,
            ticketsLast2Weeks: `${pilot.analytics?.ticketsLast2Weeks ?? 0}`,
            ticketsLastWeek: `${pilot.analytics?.ticketsLastWeek ?? 0}`,
            ticketsPreviousWeek: `${pilot.analytics?.ticketsPreviousWeek ?? 0}`,
            status,
            statusLabel: getPilotStatusLabel(status),
            pilotSource: getSafeString(pilot.pilotSource),
    };
};

export const usePilotOnboardingTableViewModel = ({
    pilotOnboardingService,
}: UsePilotOnboardingTableViewModelProps) => {
    const [rows, setRows] = useState<PilotCatalogTableRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
    const [searchField, setSearchField] = useState('');
    const [statusFilter, setStatusFilter] = useState<PilotStatusFilter>('all');
    const [isStartModalOpen, setIsStartModalOpen] = useState(false);
    const [pilotPendingClose, setPilotPendingClose] = useState<PilotCatalogTableRow | null>(null);
    const [pilotPendingConvert, setPilotPendingConvert] = useState<PilotCatalogTableRow | null>(null);
    const [selectedPilotDetail, setSelectedPilotDetail] = useState<PilotCatalogTableRow | null>(null);
    const [isClosingPilot, setIsClosingPilot] = useState(false);
    const [isConvertingPilot, setIsConvertingPilot] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        setLoadErrorMessage(null);

        try {
            const pilots = await pilotOnboardingService.getPilots();
            const normalized = pilots
                .map(normalizePilotRow)
                .sort(comparePilotsByDefaultOrder);

            setRows(normalized);
        } catch (error) {
            const message = error instanceof Error && error.message
                ? error.message
                : 'No se pudieron cargar los pilotos. Intenta nuevamente.'.localized();

            setLoadErrorMessage(message);
        } finally {
            setLoading(false);
        }
    }, [pilotOnboardingService]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const filteredRows = useMemo(() => {
        const normalizedSearch = searchField.toLowerCase().replace(/\s/g, '');
        const statusFilteredRows = rows.filter((row) => matchesPilotStatusFilter(row.status, statusFilter));

        if (!normalizedSearch) {
            return statusFilteredRows;
        }

        return statusFilteredRows.filter((row) => {
            const searchableText = [
                row.id,
                row.name,
                row.legalName,
                row.customer,
                row.countryCode,
                row.timezone,
            ]
                .join(' ')
                .toLowerCase()
                .replace(/\s/g, '');

            return searchableText.includes(normalizedSearch);
        });
    }, [rows, searchField, statusFilter]);

    const totalPilotsCount = rows.length;
    const activeThisWeekCount = useMemo(() => {
        return rows.filter((row) => row.usersLastWeekCount > 0).length;
    }, [rows]);

    return {
        rows: filteredRows,
        allRows: rows,
        loading,
        loadErrorMessage,
        searchField,
        setSearchField,
        statusFilter,
        setStatusFilter,
        totalPilotsCount,
        activeThisWeekCount,
        refresh,
        isStartModalOpen,
        openStartModal: () => setIsStartModalOpen(true),
        closeStartModal: () => setIsStartModalOpen(false),
        selectedPilotDetail,
        isDetailModalOpen: selectedPilotDetail !== null,
        openDetailModal: (pilot: PilotCatalogTableRow) => setSelectedPilotDetail(pilot),
        closeDetailModal: () => setSelectedPilotDetail(null),
        pilotPendingClose,
        isCloseModalOpen: pilotPendingClose !== null,
        isClosingPilot,
        openCloseModal: (pilot: PilotCatalogTableRow) => setPilotPendingClose(pilot),
        closeCloseModal: () => {
            if (isClosingPilot) {
                return;
            }
            setPilotPendingClose(null);
        },
        pilotPendingConvert,
        isConvertModalOpen: pilotPendingConvert !== null,
        isConvertingPilot,
        openConvertModal: (pilot: PilotCatalogTableRow) => setPilotPendingConvert(pilot),
        closeConvertModal: () => {
            if (isConvertingPilot) {
                return;
            }
            setPilotPendingConvert(null);
        },
        convertPilot: async (convertedBy?: string): Promise<ConvertPilotResult> => {
            if (!pilotPendingConvert) {
                return {
                    kind: 'error',
                    message: 'Selecciona un piloto para convertir.'.localized(),
                };
            }

            if (!pilotPendingConvert.organizationID) {
                return {
                    kind: 'error',
                    message: 'El piloto seleccionado no tiene un ID de organizacion valido.'.localized(),
                };
            }

            setIsConvertingPilot(true);

            try {
                await pilotOnboardingService.convertPilot({
                    organizationID: pilotPendingConvert.organizationID,
                    convertedBy,
                    reason: 'Pilot converted from control board',
                });

                setPilotPendingConvert(null);
                return {
                    kind: 'success',
                    message: 'Se convirtio el piloto a cliente correctamente.'.localized(),
                };
            } catch (error) {
                return {
                    kind: 'error',
                    message: error instanceof Error && error.message
                        ? error.message
                        : 'No se pudo convertir el piloto. Intenta nuevamente.'.localized(),
                };
            } finally {
                setIsConvertingPilot(false);
            }
        },
        closePilot: async (deprecatedBy?: string): Promise<ClosePilotResult> => {
            if (!pilotPendingClose) {
                return {
                    kind: 'error',
                    message: 'Selecciona un piloto para cerrar.'.localized(),
                };
            }

            if (!pilotPendingClose.organizationID) {
                return {
                    kind: 'error',
                    message: 'El piloto seleccionado no tiene un ID de organizacion valido.'.localized(),
                };
            }

            setIsClosingPilot(true);

            try {
                await pilotOnboardingService.deprecatePilot({
                    organizationID: pilotPendingClose.organizationID,
                    deprecatedBy,
                    reason: 'Pilot closed from control board',
                });

                setPilotPendingClose(null);
                return {
                    kind: 'success',
                    message: 'Se envio el cierre del piloto correctamente.'.localized(),
                };
            } catch (error) {
                return {
                    kind: 'error',
                    message: error instanceof Error && error.message
                        ? error.message
                        : 'No se pudo cerrar el piloto. Intenta nuevamente.'.localized(),
                };
            } finally {
                setIsClosingPilot(false);
            }
        },
    };
};
